import { api, types } from "api";
import { useCallback, useEffect, useState } from "react";
import { AuthStatusContext, AuthStatusRole } from "./context";
import { getMaxRole } from "./getMaxRole";

/**
 * Internal-only state for the auth status.
 */
interface AuthState {
	role: AuthStatusRole;
	isLoading: boolean;
}

interface AuthStatusProviderProps {
	children: React.ReactNode;
}

export const AuthStatusProvider = ({
	children,
}: AuthStatusProviderProps): JSX.Element => {
	const [state, setState] = useState<AuthState>({
		role: "unauthenticated",
		isLoading: true,
	});

	const setRole = useCallback(
		(role: AuthStatusRole | types.RoleType[] = "unauthenticated") => {
			setState({
				role: getMaxRole(role),
				isLoading: false,
			});
		},
		[setState],
	);

	useEffect(() => {
		api
			.authStatus()
			.then((res) => {
				setRole(
					res.status === 200 ? getMaxRole(res.data.role) : "unauthenticated",
				);
			})
			.catch(() => {
				setState({
					role: "unauthenticated",
					isLoading: false,
				});
			});
	}, [setRole]);

	return (
		<AuthStatusContext.Provider
			value={{
				isLoading: state.isLoading,
				setRole,
				role: state.role,
			}}
		>
			{children}
		</AuthStatusContext.Provider>
	);
};
